/**
 * Who a feature announcement may be addressed to.
 *
 * Operator, 2026-08-22: "I'd like a /send-feature-email: oliver". So a short
 * name has to resolve to an address — and it has to resolve SERVER-SIDE, in
 * code, not in the prompt that typed it.
 *
 * ─── Why this is a closed registry and not a free-text address ─────────────
 *
 * The whole point of the approval flow is that the operator reads a letter and
 * then releases THAT letter to a KNOWN audience. If the recipient were free
 * text, a typo would send company-internal screenshots — competitor door names,
 * dollar figures, unreleased product plans — to whoever owns the misspelling.
 * A closed list makes the failure a 400 instead of a disclosure.
 *
 * It is also what the approval TOKEN pins (see feature-email.ts): the audience
 * is fixed at review time, so the click can only release to the people the
 * operator was told about in the letter they read. A click cannot be
 * re-pointed at someone else by editing the URL.
 *
 * Adding somebody is a code change on purpose. That is the review step.
 */

export interface AnnounceRecipient {
  /** The key typed after the slash command — lowercase, no spaces. */
  key: string
  /** Display name, used in the approval banner so the operator sees WHO. */
  name: string
  email: string
}

/**
 * The people a feature announcement can go to.
 *
 * The seven Product Builders (lib/rd-launch-checks.ts) — the audience for
 * product and launch work — plus the operator.
 *
 * EVERY ADDRESS VERIFIED AGAINST THE LIVE ACL USER STORE, 2026-08-22, all
 * `status: active` — the same bar `lib/newsletter/recipients.ts` set and for
 * the same reason: a guessed address is a disclosure, not a typo.
 *
 * One near-miss worth recording: the codebase contains TWO spellings,
 * `fletcher@` and `fletche@`. The live store has only `fletcher@` (Fletcher
 * Osborne, active); `fletche@` appears in exactly two code COMMENTS quoting an
 * operator report and is not a mailbox. Grepping the repo for an address would
 * have had a 50% chance of picking the one that does not exist.
 */
export const ANNOUNCE_RECIPIENTS: readonly AnnounceRecipient[] = [
  { key: 'oliver',   name: 'Oliver Stannard',  email: 'oliver@greenlabsllc.com' },
  { key: 'jordan',   name: 'Jordan McAulay',   email: 'jordan@greenlabsllc.com' },
  { key: 'fletcher', name: 'Fletcher Osborne', email: 'fletcher@greenlabsllc.com' },
  { key: 'blade',    name: 'Blade Bolden',     email: 'blade@greenlabsllc.com' },
  { key: 'hanna',    name: 'Hanna Malinao',    email: 'hanna@greenlabsllc.com' },
  { key: 'peter',    name: 'Peter Guyton',     email: 'pete@greenlabsllc.com' },
  // The sales / field team — added 2026-08-29 (DI perf announcement); both VERIFIED
  // against the live ACL user store the same day, status:active.
  { key: 'holly',    name: 'Holly Marroquin',  email: 'holly@greenlabsllc.com' },
  { key: 'brooks',   name: 'Brooks Ortman',    email: 'brooks@greenlabsllc.com' },
  // Accounting — added 2026-09-02 for the order-to-cash announcement (the payment
  // tracker moves into the portal and she is the one who signs each invoice off).
  // VERIFIED against the live ACL user store the same day: `cristina@greenlabsllc.com`,
  // "Cristi (Froghila) Toader", status:active. NOTE the spelling trap — she is called
  // Cristi everywhere in the sheet and the UI, but the mailbox is `cristina@`; keying
  // the display name would have produced an address that does not exist. Her account
  // was invited 2026-05-01 and has never been signed in to, so the first letter she
  // gets from the portal is also her prompt to accept the invite.
  { key: 'cristi',   name: 'Cristi Toader',    email: 'cristina@greenlabsllc.com' },
  // Maintenance — added 2026-09-07 for the weekly maintenance letter. BOTH VERIFIED against the live
  // employee roster the same day: Stephen Wilkes `stephen@`, Chris Stone `chris@`, both status:active,
  // both "Maintenence L2". They are the letter's TO — it is their work it lists.
  { key: 'stephen',  name: 'Stephen Wilkes',   email: 'stephen@greenlabsllc.com' },
  { key: 'chris',    name: 'Chris Stone',      email: 'chris@greenlabsllc.com' },
  { key: 'fabian',   name: 'Fabian Toader',    email: 'fabian@greenlabsllc.com' },
] as const

/**
 * Named groups — a group KEY (typed as one token, so no spaces) expands to its member keys, so you can
 * address a whole team by name instead of listing everyone. Deliberately NOT "everyone": a group that
 * means the whole company is the one somebody reaches for by accident. Combining groups unions +
 * dedupes (e.g. `sales-team, operations`).
 *
 * Operator-defined 2026-08-29:
 *   sales-team  → Jordan, Oliver, Holly, Brooks   (the field / sell-side)
 *   operations  → Fletcher, Blade, Peter, Hanna   (the ops side)
 * `team` (the original launch/product-builder audience) is kept for back-compat.
 */
export const ANNOUNCE_GROUPS: Readonly<Record<string, readonly string[]>> = {
  'sales-team': ['jordan', 'oliver', 'holly', 'brooks'],
  'operations': ['fletcher', 'blade', 'peter', 'hanna'],
  // Added 2026-09-02 with the order-to-cash announcement. A group of one today, and
  // deliberately its own group rather than a member of `operations`: accounting sits at
  // the END of the delivery workflow (the invoice sign-off), so a letter about the
  // facility's work is not automatically a letter to the person who books the money.
  'accounting': ['cristi'],
  team: ['oliver', 'jordan', 'fletcher', 'blade', 'hanna', 'peter'],
  // Added 2026-09-07 with the weekly maintenance letter — the two people who actually do the work
  // the letter lists. Deliberately NOT part of `operations`: that group is the managers who need to
  // know, and this one is the pair who need to act.
  maintenance: ['stephen', 'chris'],
}

export interface ResolveResult {
  /** Resolved, de-duplicated, in registry order. */
  people: AnnounceRecipient[]
  /** Anything that matched neither a key nor a group. Never guessed at. */
  unknown: string[]
}

/**
 * Resolve `"oliver"` / `"oliver, jordan"` / `"team"` to addresses.
 *
 * An unknown name is REPORTED, never dropped and never approximated. Silently
 * ignoring a name the operator typed would send the letter to a smaller
 * audience than they think they addressed — and they would have no way to tell
 * from the confirmation.
 */
export function resolveAnnounceRecipients(input: string | readonly string[]): ResolveResult {
  const raw = (Array.isArray(input) ? input : String(input ?? '').split(/[,\s]+/))
    .map(s => String(s).trim().toLowerCase())
    .filter(Boolean)

  const byKey = new Map(ANNOUNCE_RECIPIENTS.map(r => [r.key, r]))
  const wanted = new Set<string>()
  const unknown: string[] = []

  for (const token of raw) {
    const group = ANNOUNCE_GROUPS[token]
    if (group) { for (const k of group) wanted.add(k); continue }
    if (byKey.has(token)) { wanted.add(token); continue }
    unknown.push(token)
  }

  // Registry order, not input order — so the banner lists people the same way
  // every time and two runs of the same command read identically.
  return {
    people: ANNOUNCE_RECIPIENTS.filter(r => wanted.has(r.key)),
    unknown,
  }
}

/** "Oliver Stannard and Jordan McAulay" — for the banner's "this will go to" line. */
export function describeRecipients(people: readonly AnnounceRecipient[]): string {
  const names = people.map(p => p.name)
  if (names.length === 0) return 'nobody'
  if (names.length === 1) return names[0]
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/** Human label for a group key: 'sales-team' → 'Sales Team'. PURE. */
export function announceGroupLabel(groupKey: string): string {
  return groupKey.split('-').map(w => (w ? w[0].toUpperCase() + w.slice(1) : w)).join(' ')
}

export interface ChooserGroup { key: string | null; label: string; people: AnnounceRecipient[] }

/**
 * Bucket the candidate people into their groups for the approval chooser, so the operator can tick a
 * whole team OR individuals. Each person lands in the FIRST group (in ANNOUNCE_GROUPS order) that
 * contains them — sales-team / operations come before the legacy `team`, so nobody is listed twice;
 * anyone in no group falls into a trailing "Other" bucket. Groups appear in first-seen order. PURE.
 */
export function groupCandidatesForChooser(people: readonly AnnounceRecipient[]): ChooserGroup[] {
  const groupKeys = Object.keys(ANNOUNCE_GROUPS)
  const firstGroupOf = (key: string): string | null => groupKeys.find(g => ANNOUNCE_GROUPS[g].includes(key)) ?? null
  const order: (string | null)[] = []
  const byGroup = new Map<string | null, AnnounceRecipient[]>()
  for (const p of people) {
    const g = firstGroupOf(p.key)
    if (!byGroup.has(g)) { byGroup.set(g, []); order.push(g) }
    byGroup.get(g)!.push(p)
  }
  const groups: ChooserGroup[] = order
    .filter((g): g is string => g !== null)
    .map(g => ({ key: g, label: announceGroupLabel(g), people: byGroup.get(g)! }))
  if (byGroup.has(null)) groups.push({ key: null, label: 'Other', people: byGroup.get(null)! })
  return groups
}
